import type { ResourceType, ResourceTypeString } from '@ctw/shared/api/fhir/types';
import type { CTWState } from '@ctw/shared/context/ctw-context';
import type { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { type Env, getApiServiceBaseUrl } from '@ctw/shared/utils/environments';
import { isEmptyValue } from '@ctw/shared/utils/types';
import type { GraphQLClient, Variables } from 'graphql-request';
import { get, isEmpty } from 'lodash-es';
import { graphQLToFHIR } from './graphql-to-fhir';

export interface GraphqlPageInfo {
  hasNextPage: boolean;
  startCursor?: string;
  endCursor?: string;
  // Exact, authorized, post-filter count across all pages. Best-effort: null/undefined when a
  // total is not available for the query (treat as unknown, not zero). Only the hotstorage read
  // path populates it, and only queries that select `total` receive it.
  total?: number | null;
}

export interface GraphqlConnectionNode<T> {
  node: T;
}

export interface GenericConnection<T extends ResourceTypeString> {
  pageInfo: GraphqlPageInfo;
  edges: Array<GraphqlConnectionNode<ResourceType<T>>>;
}

// FQS nulls a connection field when a per-field authorization check fails, and depending on the
// query shape that null can land on the connection itself or just its `edges` (a present
// connection whose `edges` field independently comes back null). Treat all of those the same
// way a paginated request already treats a missing page: as an empty connection, rather than
// letting the caller crash reading `.edges` or `.pageInfo` off something absent. This hides the
// authorization failure from the user — an empty connection here is indistinguishable from the
// patient genuinely having no records of this type.
export function connectionOrEmpty<T>(
  connection:
    | { edges?: Array<GraphqlConnectionNode<T>> | null; pageInfo?: GraphqlPageInfo | null }
    | null
    | undefined,
): { edges: Array<GraphqlConnectionNode<T>>; pageInfo: GraphqlPageInfo } {
  return {
    edges: connection?.edges ?? [],
    pageInfo: connection?.pageInfo ?? { hasNextPage: false },
  };
}

// interface FqsResponse<T extends ResourceTypeString> {
//   data: {
//     [key: T]: GenericConnection<T>;
//   };
// }

/*
 * FQS has hard limit of 500 objects per request
 */
export const MAX_OBJECTS_PER_REQUEST = 500;

export const ALL_PAGES = 9999;

export interface FqsRequestOptions {
  requestHeaders?: Record<string, string>;
}

/*
 * @deprecated This method should not be used and will be replaced soon.
 */
// In-flight request deduplication for single-page fqsRequest calls. Unlike
// fqsRequestAll (which dedupes at the pagination level), direct callers of
// fqsRequest — and Strict Mode / multi-subscriber scenarios — can fire the
// same query many times concurrently.
const inflightFqsRequests = new Map<string, Promise<{ data: unknown }>>();

export async function fqsRequest<T>(
  telemetry: ReturnType<typeof useTelemetry>,
  client: GraphQLClient,
  query: string,
  variables: object,
  options?: FqsRequestOptions,
): Promise<{ data: T }> {
  const updatedVariables = { ...variables };
  if (
    'first' in updatedVariables &&
    typeof updatedVariables.first === 'number' &&
    updatedVariables.first > MAX_OBJECTS_PER_REQUEST
  ) {
    updatedVariables.first = MAX_OBJECTS_PER_REQUEST;
  }

  const dedupeKey = `${query}:${JSON.stringify(updatedVariables)}${JSON.stringify(options)}`;
  const inflight = inflightFqsRequests.get(dedupeKey);
  if (inflight) {
    return inflight as Promise<{ data: T }>;
  }

  const promise = fqsRequestInternal<T>(telemetry, client, query, updatedVariables, options);
  inflightFqsRequests.set(dedupeKey, promise as Promise<{ data: unknown }>);
  await promise.finally(() => {
    inflightFqsRequests.delete(dedupeKey);
  });

  return promise;
}

async function fqsRequestInternal<T>(
  telemetry: ReturnType<typeof useTelemetry>,
  client: GraphQLClient,
  query: string,
  updatedVariables: object,
  options?: FqsRequestOptions,
): Promise<{ data: T }> {
  const { data, errors } = await client.rawRequest<T>(
    query,
    updatedVariables as Variables,
    options?.requestHeaders,
  );

  void telemetry.trackMetric('fqs_request', {
    query,
    variables: updatedVariables,
  });

  const fhirData = graphQLToFHIR(data);
  if (errors) {
    const requestCompletelyFailed = isEmptyValue(data);
    // FQS emits the bare string `unauthorized` for any per-field authorization check, with no
    // way to distinguish it from a real outage. When every error on an otherwise-total failure
    // is `unauthorized`, treat the request as succeeding with no data instead of failing —
    // matching how a paginated request already treats a missing connection as empty. This hides
    // the authorization failure from the caller.
    const isUnauthorizedOnly = errors.every((error) => error.message === 'unauthorized');

    telemetry.logger.error(
      requestCompletelyFailed
        ? isUnauthorizedOnly
          ? 'FQS request failed with only unauthorized errors, returning empty result'
          : 'FQS request failed'
        : 'FQS request partially failed, proceeding with returned data',
      {
        requestCompletelyFailed,
        isUnauthorizedOnly,
        errors: errors.slice(0, 10),
        errorsTruncated: errors.length > 10,
      },
    );
    if (requestCompletelyFailed) {
      if (isUnauthorizedOnly) {
        // GraphQL null-bubbling can null `data` itself (not just the failed field) when the
        // query has a single non-null connection field, so `fhirData` here can be `null` rather
        // than an object with the key merely absent. Force an empty object so callers see the
        // same shape as the partial-failure case above: a key that's missing, not a `data` that
        // is itself null one property earlier than any `.edges` guard would catch.
        return { data: {} as T };
      }
      // `errors` is already an array of `GraphQLError`, which extends `Error` — wrapping in
      // `AggregateError` makes the thrown value a proper `Error` (so it surfaces through
      // react-query's `onError` instead of an unhandled rejection) without discarding anything.
      throw new AggregateError(errors, 'FQS request failed');
    }
  }
  return { data: fhirData };
}

/*
 * @deprecated This method should not be used and will be replaced soon.
 */
type FqsRequestAllResult<T> = { data: T; fqsPagesFetched: number; fqsHadMorePages: boolean };

// In-flight request deduplication: if fqsRequestAll is called multiple times
// for the same query+namespace+upid while a request is already in progress,
// subsequent callers share the existing promise instead of starting new
// concurrent pagination loops.
const inflightRequests = new Map<string, Promise<FqsRequestAllResult<unknown>>>();

export async function fqsRequestAll<T>(
  telemetry: ReturnType<typeof useTelemetry>,
  client: GraphQLClient,
  query: string,
  namespace: string,
  variables: object,
  pageLimit = ALL_PAGES,
  options?: FqsRequestOptions,
): Promise<FqsRequestAllResult<T>> {
  const dedupeKey = `${namespace}:${pageLimit}:${query}:${JSON.stringify(variables)}${JSON.stringify(options)}`;

  const inflight = inflightRequests.get(dedupeKey);
  if (inflight) {
    return inflight as Promise<FqsRequestAllResult<T>>;
  }

  const promise = fqsRequestAllInternal<T>(
    telemetry,
    client,
    query,
    namespace,
    variables,
    pageLimit,
    options,
  );
  inflightRequests.set(dedupeKey, promise as Promise<FqsRequestAllResult<unknown>>);
  await promise.finally(() => {
    inflightRequests.delete(dedupeKey);
  });

  return promise;
}

async function fqsRequestAllInternal<T>(
  telemetry: ReturnType<typeof useTelemetry>,
  client: GraphQLClient,
  query: string,
  namespace: string,
  variables: object,
  pageLimit: number,
  options?: FqsRequestOptions,
): Promise<FqsRequestAllResult<T>> {
  if (!isEmpty(get(variables, 'sort'))) {
    throw new Error(
      "'sort' variable not allowed in fqsRequestAll; FQS does not support sorting with pagination",
    );
  }
  let pageCount = 0;
  let fqsHasNextPageClaim = true;
  const allResponses: Array<{ data: T }> = [];
  let lastEndCursor = '';

  const processResponses = (responses: Array<{ data: T }>) =>
    responses.reduce(
      (acc, next) => {
        // Defaults to `[]` when a page's connection is absent (e.g. an `unauthorized` field
        // error on that page), so a paginated request already treats a missing connection as an
        // empty page rather than crashing on it.
        const edges = get(acc, ['data', namespace, 'edges'], []);
        const nextEdges = get(next, ['data', namespace, 'edges'], []);

        return {
          ...acc,
          data: {
            [namespace]: {
              edges: edges.concat(nextEdges),
            },
          } as T,
        };
      },
      { data: { [namespace]: { edges: [] } } } as { data: T },
    );

  do {
    const updatedVariables = {
      ...variables,
      // Some queries require 'sort', but if it is set to anything besides an empty object, fqs pagination breaks!
      sort: {},
      cursor: lastEndCursor,
    };
    const response = await fqsRequest<T>(telemetry, client, query, updatedVariables, options);
    allResponses.push(response);
    const endCursor = get(response, ['data', namespace, 'pageInfo', 'endCursor'], '');
    fqsHasNextPageClaim =
      lastEndCursor !== endCursor &&
      get(response, ['data', namespace, 'pageInfo', 'hasNextPage'], false);

    lastEndCursor = endCursor;
    pageCount += 1;
  } while (pageLimit > pageCount && fqsHasNextPageClaim);

  // This metric informs how many pages we've fetched and whether we finished early with still more pages to fetch.
  telemetry.trackMetric('fqs_request_all', {
    pages_fetched: pageCount,
    has_next_page: fqsHasNextPageClaim,
    query_type: namespace,
  });

  const processedResponses = processResponses(allResponses);

  return {
    data: processedResponses.data,
    fqsHadMorePages: fqsHasNextPageClaim,
    fqsPagesFetched: pageCount,
  };
}

/*
 * @deprecated This method should not be used and will be replaced soon.
 */
export function getFetchFromFqs(ctwFetch: CTWState['ctwFetch'], env: Env, accessToken: string) {
  return (url: string, options: RequestInit) =>
    ctwFetch(`${getApiServiceBaseUrl({ service: 'fqs', env })}/${url}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...options.headers,
      } as Record<string, string>,
    });
}
